"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DropdownOverlay = exports.DropdownPositionedAboveContext = void 0;
var tslib_1 = require("tslib");
var React = tslib_1.__importStar(require("react"));
var tabbable_1 = require("tabbable");
var tslib_2 = require("tslib");
var StackingComponent_1 = require("../../utils/StackingComponent");
var ViewportHelpers_1 = require("../../utils/ViewportHelpers");
var DropdownButtonStyles_1 = require("./DropdownButtonStyles");
/**
 * * `true` when a dropdown popup is positioned above (rather than below) its button.
 * * `false` when below.
 * * `undefined` if a dropdown popup is not an ancestor of the component consuming this Context.
 */
exports.DropdownPositionedAboveContext = React.createContext(undefined);
exports.DropdownPositionedAboveContext.displayName = 'DropdownPositionedAboveContext';
/** @private Internal, private implementation detail of the `DropdownButton` widget. Unsupported. Do not use. */
var DropdownOverlay = /** @class */ (function (_super) {
    tslib_1.__extends(DropdownOverlay, _super);
    function DropdownOverlay() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.ref = React.createRef();
        _this.state = { positionedAbove: false };
        return _this;
    }
    DropdownOverlay.prototype.componentDidMount = function () {
        var _this = this;
        this.reposition();
        // we don't want to focus the element until the styling/layout has settled and painted, otherwise the viewport tends to jump
        window.setTimeout(function () {
            _this.focusFirstTabbable();
        });
    };
    DropdownOverlay.prototype.componentDidUpdate = function (prevProps) {
        if (prevProps.getAnchorClientRect !== this.props.getAnchorClientRect || prevProps.alignment !== this.props.alignment) {
            this.reposition();
        }
    };
    DropdownOverlay.prototype.focusFirstTabbable = function () {
        var _a;
        var mountPoint = (_a = this.ref.current) === null || _a === void 0 ? void 0 : _a.getMountPoint();
        if (!mountPoint) {
            return;
        }
        // falls back to focusing the first focusable if there is nothing tabbable
        var firstTabbable = tabbable_1.tabbable(mountPoint)[0] || mountPoint.querySelector('[tabindex]');
        firstTabbable === null || firstTabbable === void 0 ? void 0 : firstTabbable.focus();
    };
    /**
     * Repositions the popup. Might be useful if the popup contents weren't fully sized at the time of attachment,
     * e.g. an image still loading. This is excluded from React render() path because we generally want it to stay put after attaching.
     */
    DropdownOverlay.prototype.reposition = function () {
        var positioningElement = this.ref.current.getMountPoint();
        var newPosition = this.getPosition();
        tslib_2.__assign(positioningElement.style, newPosition);
        this.setState({ positionedAbove: !!newPosition.bottom });
    };
    DropdownOverlay.prototype.getPosition = function () {
        var popupElem = this.ref.current.getMountPoint();
        var _a = popupElem.getBoundingClientRect(), popupWidth = _a.width, popupHeight = _a.height;
        // it's called a dropdown, not a dropup, so prefer to attach the popup below, unless
        // (1) doesn't fit below and (2) there is more space above than below
        var anchorRect = this.props.getAnchorClientRect();
        var viewport = ViewportHelpers_1.getVisualViewport();
        var spaceBelow = viewport.offsetTop + viewport.height - anchorRect.bottom;
        var spaceAbove = anchorRect.top - viewport.offsetTop;
        // 'top'/'bottom' here read unintuitively but they refer to the css property, so attaching above the anchor means using 'bottom'
        var verticalAttachment = (popupHeight > spaceBelow && spaceAbove > spaceBelow) ? 'bottom' : 'top';
        // we allow an external preference for left vs right alignment via a prop, but still switch if we would end up out-of-bounds
        var fitsLeft = anchorRect.left + popupWidth < viewport.offsetLeft + viewport.width;
        var fitsRight = anchorRect.right - popupWidth > viewport.offsetLeft;
        var horizontalAttachment = this.props.alignment;
        if (horizontalAttachment === 'left' && !fitsLeft && fitsRight) {
            horizontalAttachment = 'right';
        }
        else if (horizontalAttachment === 'right' && !fitsRight && fitsLeft) {
            horizontalAttachment = 'left';
        }
        // we can't know apriori what the nearest positioned ancestor is (or if we can, it's too nasty to figure out), so instead
        // we attach the popup and then calculate the necessary repositioning relative to where it first appears
        popupElem.style[horizontalAttachment] = '0';
        popupElem.style[verticalAttachment] = '0';
        var popupRect = popupElem.getBoundingClientRect();
        // we always set all four properties because we need to clear all previous positioning, otherwise we'll be specifying the whole
        // popup rectangle rather than just its attachment+position
        var positionStyle = {
            top: verticalAttachment === 'top' ? anchorRect.bottom - popupRect.top - DropdownButtonStyles_1.BorderWidth + 'px' : '',
            bottom: verticalAttachment === 'bottom' ? popupRect.bottom - anchorRect.top - DropdownButtonStyles_1.BorderWidth + 'px' : '',
            left: horizontalAttachment === 'left' ? anchorRect.left - popupRect.left + 'px' : '',
            right: horizontalAttachment === 'right' ? popupRect.right - anchorRect.right + 'px' : '',
        };
        return positionStyle;
    };
    DropdownOverlay.prototype.render = function () {
        var _a = this.props, alignment = _a.alignment, children = _a.children, getAnchorClientRect = _a.getAnchorClientRect, stackingComponentProps = tslib_1.__rest(_a, ["alignment", "children", "getAnchorClientRect"]);
        return (React.createElement(StackingComponent_1.StackingComponentWrapper, tslib_1.__assign({}, stackingComponentProps, { ref: this.ref, wrapperTestId: 'DropdownOverlay' }),
            React.createElement(exports.DropdownPositionedAboveContext.Provider, { value: this.state.positionedAbove }, children)));
    };
    return DropdownOverlay;
}(React.PureComponent));
exports.DropdownOverlay = DropdownOverlay;
//# sourceMappingURL=DropdownOverlay.js.map